import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import SaveActionButton from 'components/SaveActionButton';
import * as React from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Accordion, Button, Table } from 'semantic-ui-react';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import { getAllSections } from 'services/sectionServices';
import styled from 'styled-components';
import { MandateSectionDto, TarmedSectionDto } from 'ts-types/api.types';
import { mustBeNumber, required } from 'utils/validatorUtils';

const StyledTableCell = styled(Table.Cell)`
 height: 48.5px;
`;

const StyledField = styled(Field)`
  width: 70px;
`;

const addButton = {
  marginBottom: 0,
  marginTop: 10
};

interface Props extends MessageSourceProps {
  sections: MandateSectionDto[],
  setEditSection: (editSection: boolean) => void
}

interface State {
  activeIndex: number,
  editSection: boolean,
  editSectionIndex?: number,
  sectionCopy?: Partial<MandateSectionDto>,
  tarmedSections: TarmedSectionDto[],
  errors: Array<boolean>
}

class DignitySection extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      activeIndex: -1,
      editSection: false,
      tarmedSections: [],
      errors: [false]
    };

    getAllSections().then(tarmedSections => {
      tarmedSections.sort((a, b) => {
        return (a.active === b.active) ? 0 : a.active ? -1 : 1;
      });
      this.setState({tarmedSections: tarmedSections});
    });
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {activeIndex, editSection, editSectionIndex, tarmedSections, errors} = this.state;

    return (
        <Accordion fluid>
          <Accordion.Title active={activeIndex === 0}
                           index={0}
                           onClick={this.toggleAccordion}>
            <h3><Icon name='dropdown'/>{getMessage("mandate.sections")}</h3>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0}>
            <Table celled selectable size='small'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={3}>{getMessage('mandate.code')}</Table.HeaderCell>
                  <Table.HeaderCell width={2}>{getMessage('mandate.eanNumber')}</Table.HeaderCell>
                  <Table.HeaderCell width={2}>{getMessage('actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <FieldArray name="mandateSections">
                  {({fields}) =>
                      fields.map((field, index) => {
                        const section: MandateSectionDto = fields.value[index];

                        if (editSection && editSectionIndex === index) {
                          let dropDownHasSelectedElement = false;
                          let dropdownOptions: Array<DropdownOption> = tarmedSections
                              .filter(x => {
                                const returnVal = x.active || (section.code === x.code && !dropDownHasSelectedElement);
                                if (section.code === x.code) {
                                  dropDownHasSelectedElement = true;
                                }
                                return returnVal;
                              })
                              .map(x => {
                                return {
                                  key: x.code + x.active,
                                  text: x.code + ", " + x.text,
                                  value: x.code
                                }
                              });

                          return (
                              <Table.Row key={index}>
                                <Table.Cell width={3}>
                                  <Field
                                      name={`${field}.code`}
                                      component={Select}
                                      options={dropdownOptions}
                                      validate={required}
                                      error={errors[0]}
                                      fluid
                                  />
                                </Table.Cell>
                                <Table.Cell width={2}>
                                  <StyledField
                                      name={`${field}.eanNumber`}
                                      component={Input}
                                      validate={mustBeNumber}
                                      size='mini'
                                  />
                                </Table.Cell>
                                <Table.Cell width={2}>
                                  <SaveActionButton hoverMessage={getMessage('button.save')}
                                                    onConfirm={() => this.saveSection(fields, index)}/>
                                  <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                      onConfirm={() => this.cancelEditSection(fields, index)}/>
                                </Table.Cell>
                              </Table.Row>
                          )
                        } else {
                          const tarmedSection = tarmedSections.find(ts => ts.code === section.code);
                          const codeCellText = tarmedSection ?
                              tarmedSection.code + ", " + tarmedSection.text :
                              "";
                          return (
                              <Table.Row key={index}>
                                <StyledTableCell width={2}>
                                  {codeCellText}
                                </StyledTableCell>
                                <Table.Cell width={2}>
                                  {section.eanNumber}
                                </Table.Cell>
                                <Table.Cell width={2}>
                                  <EditActionButton
                                      hoverMessage={getMessage('msgEdit')}
                                      onConfirm={() => this.editSection(index, fields, section)}
                                  />
                                  <DeleteActionButton
                                      hoverMessage={getMessage('msgDelete')}
                                      onConfirm={() => this.deleteSection(fields, index)}
                                  />
                                </Table.Cell>
                              </Table.Row>
                          )
                        }
                      })
                  }
                </FieldArray>
              </Table.Body>
            </Table>
            <FieldArray name="mandateSections">
              {({fields}) => (
                  <Button style={addButton} compact primary onClick={(e) => this.addSection(e, fields, fields.length!)}>
                    {this.props.getMessage('button.add')}
                  </Button>
              )}
            </FieldArray>
          </Accordion.Content>
        </Accordion>
    );
  }

  toggleAccordion = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {editSection, activeIndex} = this.state;

    if (!editSection) {
      const newIndex = activeIndex === index ? -1 : index;
      this.setState({activeIndex: newIndex})
    }
  };

  saveSection = (fields: any, index: number) => {
    const {setEditSection} = this.props;

    this.validateFields(fields.value[index]);

    if (fields.valid) {
      this.setState({
        editSection: false,
        editSectionIndex: undefined,
        sectionCopy: undefined
      });
      setEditSection(false);
    }
  };

  editSection = (index: number, fields: any, section: MandateSectionDto): void => {
    const {sectionCopy, editSectionIndex} = this.state;
    const {setEditSection} = this.props;

    if (sectionCopy) {
      fields.remove(editSectionIndex);
      fields.insert(editSectionIndex, sectionCopy);
    }

    this.setState({
      editSection: true,
      editSectionIndex: index,
      sectionCopy: section
    });

    setEditSection(true);
  };

  cancelEditSection = (fields: any, index: number) => {
    const {sectionCopy} = this.state;
    const {setEditSection} = this.props;

    fields.remove(index);
    if (sectionCopy) {
      fields.insert(index, sectionCopy);
    }

    this.setState({
      editSection: false,
      editSectionIndex: undefined,
      sectionCopy: undefined
    });

    setEditSection(false);
  };

  deleteSection = (fields: any, index: number) => {
    const {sectionCopy, editSectionIndex} = this.state;
    const {setEditSection} = this.props;


    if (editSectionIndex) {
      fields.remove(editSectionIndex);
      if (sectionCopy) {
        fields.insert(index, sectionCopy);
      }
      setEditSection(false);
    }
    fields.remove(index);
    this.setState({
      editSection: false,
      editSectionIndex: undefined,
      sectionCopy: undefined
    });

  };

  addSection = (e: any, fields: any, index: number) => {

    e.preventDefault();
    const {editSection, sectionCopy} = this.state;
    const {setEditSection} = this.props;


    if (fields.valid && ((sectionCopy !== undefined && editSection) || !editSection)) {
      fields.push({
        code: "",
        eanNumber: ""
      });

      this.setState({
        editSection: true,
        editSectionIndex: index ? index : 0,
        sectionCopy: undefined
      });

      setEditSection(true);
    }
  };

  validateFields = (section: Partial<MandateSectionDto>): boolean => {
    let errors: Array<boolean> = [];

    errors[0] = section.code == "";

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

}

export default withMessages(DignitySection);
